import CONFIG from "../../../config";

const fileService = CONFIG.FILE_SERVICE;

/** Shared height for the header bar across all file page columns so borders align. */
export const FILES_HEADER_HEIGHT = 48;

export function parseDate(dateValue) {
  if (!dateValue) return null;
  try {
    let date;
    if (typeof dateValue === "number" || typeof dateValue === "string") {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else if (Array.isArray(dateValue)) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else if (typeof dateValue === "object" && dateValue.$date) {
      date = new Date(dateValue.$date);
    } else {
      return null;
    }
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

export function formatDateTime(dateTime) {
  const date = parseDate(dateTime);
  if (!date) return "-";
  try {
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateTime);
  }
}

export function formatRelativeTime(dateTime) {
  const date = parseDate(dateTime);
  if (!date) return "-";
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  return formatDateTime(dateTime);
}

export function formatBytes(bytes, decimals = 2) {
  if (!bytes || bytes === 0) return "0 Bytes";
  if (bytes < 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function getFileExtension(fileName) {
  if (!fileName) return "";
  const parts = fileName.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
}

export function getFileCategory(fileName) {
  const ext = getFileExtension(fileName).toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"].includes(ext)) return "image";
  if (["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "rtf"].includes(ext)) return "document";
  if (["js", "jsx", "ts", "tsx", "py", "java", "go", "rb", "php", "c", "cpp", "h", "css", "html", "xml", "yaml", "yml", "toml", "sh", "bash"].includes(ext)) return "code";
  if (["mp4", "webm", "mov", "avi", "mkv", "flv", "wmv"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "flac", "aac", "m4a", "wma"].includes(ext)) return "audio";
  if (["zip", "tar", "gz", "rar", "7z", "bz2"].includes(ext)) return "archive";
  if (["csv", "json", "txt", "md", "log"].includes(ext)) return "text";
  return "other";
}

export function normalizeFiles(inputFiles = []) {
  const filtered = inputFiles.filter((f) => f.configId !== "zipfiles");
  filtered.sort((a, b) => {
    const dateA = parseDate(a.createDate);
    const dateB = parseDate(b.createDate);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateB - dateA;
  });
  const totalSize = filtered.reduce((sum, f) => sum + (f.fileSize || 0), 0);
  return { filtered, totalSize };
}

export function buildFileUrl(fileId) {
  return `${fileService.QUERY_URL}/${fileId}`;
}

export async function copyFileLink(fileId) {
  const link = buildFileUrl(fileId);
  try {
    await navigator.clipboard.writeText(link);
    return true;
  } catch {
    return false;
  }
}
